import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';

// These mirror Messages::EditService on the server, which is the authority.
// They exist here only so the dashboard offers the option when it will work and
// stays quiet when it will not.
export const CUSTOMER_FACING_EDIT_WINDOW = 2 * 60 * 1000;
export const PRIVATE_NOTE_EDIT_WINDOW = 24 * 60 * 60 * 1000;

// A message reaches this file as a store record in snake case or as component
// props in camel case, so the accessors read either, as the context menu
// already does for the same reason.
const messageType = message => message?.message_type ?? message?.messageType;
const contentAttributes = message =>
  message?.content_attributes ?? message?.contentAttributes ?? {};
const sentAtSeconds = message => message?.created_at ?? message?.createdAt;
const isPrivateNote = message => Boolean(message?.private);

export const editWindowFor = message =>
  isPrivateNote(message)
    ? PRIVATE_NOTE_EDIT_WINDOW
    : CUSTOMER_FACING_EDIT_WINDOW;

/**
 * Milliseconds left before the message can no longer be edited.
 *
 * @param {object} message
 * @param {number} now epoch milliseconds, injectable for tests
 * @returns {number} zero once the window has passed
 */
export const remainingEditTime = (message, now = Date.now()) => {
  const sentAt = sentAtSeconds(message);
  if (!sentAt) return 0;
  // Both shapes carry the send time as unix seconds.
  return Math.max(0, sentAt * 1000 + editWindowFor(message) - now);
};

/**
 * Whether the dashboard should offer to edit this message.
 *
 * @param {object} params
 * @param {object} params.message the message in question
 * @param {boolean} params.isWebWidgetInbox whether the conversation is live chat
 * @param {number} [params.latestIncomingMessageId] the customer's newest message
 * @param {number} [params.now] epoch milliseconds, injectable for tests
 * @returns {boolean}
 */
export const isMessageEditable = ({
  message,
  isWebWidgetInbox,
  latestIncomingMessageId,
  now = Date.now(),
}) => {
  if (!message) return false;
  if (messageType(message) !== MESSAGE_TYPE.OUTGOING) return false;
  if (!message.content) return false;
  if (contentAttributes(message).deleted) return false;
  if (
    message.status === MESSAGE_STATUS.FAILED ||
    message.status === MESSAGE_STATUS.PROGRESS
  ) {
    return false;
  }
  if (remainingEditTime(message, now) === 0) return false;

  // A private note never leaves the team, so neither the channel nor a later
  // message makes correcting it misleading.
  if (isPrivateNote(message)) return true;

  if (!isWebWidgetInbox) return false;
  // Only a customer reply closes the door. The agent's own later messages do
  // not, so the first of a quick burst stays as correctable as the last.
  return !latestIncomingMessageId || latestIncomingMessageId < message.id;
};
